import UpgradeCostCalculator from "./UpgradeCostCalculator";
import { CountToPurchase } from "./CountToPurchase";
import BigNum from "../../math/BigNum";
import Upgrade from "../Upgrade";

export default class ClassicUpgrade extends Upgrade {
    /**
     * @abstract
     * @param {string} id
     * @param {string} title
     * @param {string} iconFileName
     * @param {UpgradeCostCalculator} costCalculator 
     * @param {number} maxCount Set to -1 for no max count.
     */
    constructor(id, title, iconFileName, costCalculator, maxCount) {
        super(title, iconFileName);

        /**
         * @constant
         * @type {string}
         */
        this.id = id;

        /**
         * @constant
         * @type {UpgradeCostCalculator}
         */
        this.costCalculator = costCalculator;

        /**
         * @constant
         * @type {number}
         */
        this.maxCount = maxCount;
    
        /**
         * @type {BigNum}
         */
        this.singlePurchaseCost = new BigNum(0);
    
        /**
         * The current cost, based on the 'count to purchase'.
         * @type {BigNum}
         */
        this.cost = new BigNum(0);

        /**
         * @type {number}
         */
        this.countToPurchase = CountToPurchase.purchaseOne;
        /**
         * @type {number}
         */
        this.countToPurchaseUsedInCalculation = CountToPurchase.purchaseOne; // the count to purchase setting used to determine the cost.
    
        /**
         * This is a calculated value.
         * @type {number}
         */
        this.maxAffordableCount = 0;

        /**
         * This is a calculated value.
         * @type {number}
         */
        this.nextActualPurchaseCount = 0;

        /**
         * @type {BigNum}
         */
        this.workingCost = new BigNum(0);
    }

    initializeCosts() {
        this.calculateCostsAfterCountChange(); // depends on: cost factor, initial cost
    }

    /**
     * @abstract
     * @returns {boolean}
     */
    isVisible() {
        return true;
    }

    /**
     * @returns {boolean}
     */
    isMaxCountReached() {
        const maxCount = this.getMaxPurchaseCount();
        return maxCount > 0 && this.purchasedCount >= maxCount;
    }

    /**
     * @returns {number}
     */
    getMaxPurchaseCount() {
        return this.maxCount;
    }

    /**
     * @override
     * @returns {number}
     */
    getPurchasedCount() {
        return this.purchasedCount;
    }

    /**
     * @override
     * @param {number} purchasedCount 
     */
    setPurchasedCount(purchasedCount) {
        // Make sure the count does not exceed max count.
        this.purchasedCount = this.maxCount > 0 ? Math.min(this.maxCount, purchasedCount) : purchasedCount;

        this.calculateCostsAfterCountChange();
    }

    /**
     * @override
     * @param {number} purchasedCount The number of upgrade purchases that have been made. 
     */
    setPurchasedCountFromSave(purchasedCount) {
        // Update the purchased count.
        this.setPurchasedCount(purchasedCount);

        // Let the subclass know that a purchase has been made.
        this.onUpgradePurchased(0, this.purchasedCount);
    }

    /**
     * @returns {BigNum}
     */
    getSinglePurchaseCost() {
        return this.singlePurchaseCost;
    }

    // /**
    //  * @param {number} countToPurchaseSetting See CountToPurchase
    //  * @param {BigNum} out
    //  * @returns {BigNum}
    //  */
    // getCostForCount(countToPurchaseSetting, out) {
    //     return this.getCostForActualCount(this.getActualCountToPurchase(countToPurchaseSetting), out);
    // }

    /**
     * @private
     * @param {number} actualPurchaseCount 
     * @param {BigNum} out
     * @returns {BigNum}
     */
    getCostForActualCount(actualPurchaseCount, out) { 
        if (actualPurchaseCount <= 0) {
            out.setZero();
            return out;
        }

        if (actualPurchaseCount === 1) {
            out.copy(this.singlePurchaseCost);
            return out;
        } else {
            // this will update 'out' and return it
            return this.costCalculator.calculateBulkPurchaseCost(this.singlePurchaseCost, actualPurchaseCount, out);
        }
    }

    /**
     * Returns the number of things to purchase. taking 'max' into consideration.
     * @returns {number}
     */
    getPurchaseCountNumber() {
        return this.getActualCountToPurchase(this.getCountToPurchaseSetting());
    }

    /**
     * The number of things to purchase, taking 'CountToPurchase.max' and max purchase count into consideration
     * @private
     * @param {number} countToPurchase See CountToPurchase
     * @returns {number}
     */
    getActualCountToPurchase(countToPurchase) {
        if (countToPurchase === CountToPurchase.purchaseMax) {
            return this.maxAffordableCount;
        } else {
            const maxCount = this.getMaxPurchaseCount();
            const currentCount = this.getPurchasedCount();
            if (currentCount + countToPurchase <= maxCount) {
                return countToPurchase;
            }
            else {
                return maxCount - currentCount;
            }

            ///return countToPurchase;  // <-- this is not taking max purchaes count into consideration
        }
    }

    /**
     * @param {number} countToPurchase See CountToPurchase
     * @returns {boolean} true if the purchase was made.
     */
    purchaseAmount(countToPurchase) {
        return this.purchaseActualAmount(this.getActualCountToPurchase(countToPurchase));
    }

    /**
     * @param {number} purchaseCount A positive integer.
     * @returns {boolean} true if the purchase was made.
     */
    purchaseActualAmount(purchaseCount) {

        //console.log('ClassicUpgrade.purchaseActualAmount() purchaseCount=' + purchaseCount);

        const maxCount = this.getMaxPurchaseCount();
        const currentCount = this.getPurchasedCount();
        if (maxCount > 0 && currentCount >= maxCount) {
            return false;
        }

        // Make sure we aren't purchasing more than what is available.
        if (maxCount > 0 && purchaseCount + currentCount > maxCount) {
            purchaseCount = maxCount - currentCount;
        }

        //console.log('ClassicUpgrade.purchaseActualAmount() adjusted purchaseCount=' + purchaseCount);

        const costForCount = this.getCostForActualCount(purchaseCount, this.workingCost); // pass in our working cost object
        if (costForCount.lessThanEqualsZero()) {
            return false;
        }

        //console.log('ClassicUpgrade.purchaseActualAmount() cost=' + NumberFormatter.formatBigNum(costForCount));

        // Get the cost of the purchase, make sure we an afford it.
        if (costForCount.greaterThan(this.getMoney())) {
            return false;
        }

        // Subtract the cost from the total money.
        this.decrementMoney(costForCount);

        // Let the subclass know that a purchase has been made
        this.onUpgradePurchased(currentCount, purchaseCount);

        // Update the building count.
        this.setPurchasedCount(currentCount + purchaseCount);

        return true;
    }

    /**
     * @private 
     */
    calculateCostsAfterCountChange() {

        // Calculate the singlePurchaseCost
        this.costCalculator.getNextPurchaseCost(this.getPurchasedCount(), this.singlePurchaseCost);

        // The 'max' cost is calculated differently than the others.
        const countToPurchaseSetting = this.getCountToPurchaseSetting();
        if (countToPurchaseSetting === CountToPurchase.purchaseMax) {
            this.recalculateMaxPurchaseCount();

            this.nextActualPurchaseCount = this.maxAffordableCount;
        } else {
            this.nextActualPurchaseCount = this.getActualCountToPurchase(countToPurchaseSetting);
            this.cost = this.getCostForActualCount(this.nextActualPurchaseCount, this.cost);
            //////this.cost = this.getCostForCount(countToPurchaseSetting, this.cost);
        }
    }

    /**
     * Figure out how many upgrades can be purchased right now based on the total money.
     * This updates the maxAffordableCount and costMax properties.
     */
    recalculateMaxPurchaseCount() {
        const money = this.getMoney();

        this.maxAffordableCount = this.costCalculator.calculateMaxAffordable(money, this.singlePurchaseCost);
        if (this.maxAffordableCount > 0) {

            const count = this.getPurchasedCount();
            const maxCount = this.getMaxPurchaseCount();
            if (maxCount > 0 && count + this.maxAffordableCount > maxCount) {
                this.maxAffordableCount = Math.max(0, maxCount - count);
                if (this.maxAffordableCount === 0) {
                    this.cost.copy(this.singlePurchaseCost);
                    return;
                }
            }

            this.cost = this.costCalculator.calculateBulkPurchaseCost(this.singlePurchaseCost, this.maxAffordableCount, this.cost);
        } else {
            // If we cannot afford a single purchase, then we should set the cost to be equal to a single purchase.
            this.cost.copy(this.singlePurchaseCost);
        }
    }

    /////////////////////////////////////////////
    // Abstract methods for our sub-classes
    /////////////////////////////////////////////

    /**
     * Returns the setting for the count to purchase.  See the CountToPurchase enum.
     * @abstract
     * @returns {number}
     */
    getCountToPurchaseSetting() {
        return this.countToPurchase; 
    }

    /**
     * @abstract
     * @returns {BigNum}
     */
    getMoney() {}

    /**
     * @abstract
     * @param {BigNum} money 
     */
    decrementMoney(money) {}

    /**
     * Invoked by the base class to let the sub-class know that a purchase has been made.
     * @abstract
     * @param {number} currentCount This is the before value.
     * @param {number} purchaseCount 
     */
    onUpgradePurchased(currentCount, purchaseCount) {}

    /////////////////////////////////////////////
    // Upgrade implementation
    /////////////////////////////////////////////

    /**
     * @override
     * @returns {boolean}
     */
    isPurchasable() {
        return this.isAffordable() && !this.isMaxCountReached();
    }
    
    /**
     * @override
     * @returns {boolean}
     */
    isAffordable() {
        return this.cost.lessThanEquals(this.getMoney());
    }

    /**
     * @override
     * @returns {BigNum}
     */
    getCost() {
        const countToPurchaseSetting = this.getCountToPurchaseSetting();
        if (this.countToPurchaseUsedInCalculation !== countToPurchaseSetting) {
            this.countToPurchaseUsedInCalculation = countToPurchaseSetting;
            this.calculateCostsAfterCountChange();
        }
        else if (countToPurchaseSetting === CountToPurchase.purchaseMax) {
            this.calculateCostsAfterCountChange();
        }
        return this.cost;
    }

    /**
     * @override
     * @returns {boolean} true if the purchase was made.
     */
    purchase() {
        return this.purchaseAmount(this.getCountToPurchaseSetting());
    }


    resetUpgrade() {
        super.resetUpgrade();
        this.calculateCostsAfterCountChange();        
    }
}